
import useFeedbackStore from "../store/useFeedbackStore";
import { FileText } from "lucide-react";
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_ACCEPT_STRING } from "../constants/fileConstants";
import { getUploadLimitError } from "./fileUploadValidation";
import { normalizeMarkdownMath } from "./markdownMathNormalizer";

// Re-export for backward compatibility
export { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_ACCEPT_STRING };

// Friendly UI messages for policy/quota denial error codes.
// Used by both the transient inline error (messageOperations.js) and the persisted
// answer doc render (SingleMessage.js) so copy lives in one place only.
export const DENIAL_ERROR_MESSAGES = {
  resource_denied_by_group: "You don't have access to this resource. Contact your admin.",
  quota_rpm_exceeded: "You've hit your rate limit. Please wait a moment and try again.",
  quota_daily_exceeded: "You've reached your daily usage limit.",
  quota_monthly_exceeded: "You've reached your monthly usage limit.",
  quota_exceeded: "You've exceeded your usage quota.",
  generation_limit_reached: (limit = 5) => `You have ${limit} active generations running. Stop one to start a new one.`,
};

export const getInitials = (text) => {
  if (!text) return "";
  const words = text.trim().split(/\s+/).filter(word => word.length > 0);
  if (words.length >= 2) {
    return words[0][0] + words[1][0];
  } else if (words.length === 1 && words[0].length >= 2) {
    return words[0].slice(0, 2);
  } else if (words.length === 1 && words[0].length === 1) {
    return words[0][0];
  }
  return "";
};

export function timeConverter(inputDate) {
  const now = new Date();
  const date = new Date(inputDate);
  const diffMs = now - date;

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (minutes < 60) {
    return `${minutes} min ago`;
  }

  if (hours < 24) {
    return `${hours} hr. ago`;
  }

  if (days < 7) {
    return date.toLocaleDateString("en-US", { weekday: "long" }); // e.g., "Monday"
  }

  return date.toLocaleDateString("en-US", {
    month: "short", // "Jan", "Feb", etc.
    day: "2-digit",
    year: "numeric",
  }); // e.g., "Mar 30, 2023"
}

export const getPastDate = (minutesAgo) => {
  const now = new Date();
  return new Date(now.getTime() - minutesAgo * 60 * 1000).toISOString();
};

export const validateFileOnCreateSpectra = (
  file,
  files = [],
  fileSizeAllowed = 7,
  totalFilesAllowed = 5,
  overFileSizeAllowed = 10,
  allowedExtensions = ALLOWED_FILE_EXTENSIONS
) => {
  if (!file) return false;
  const fileExtension = (file.name || "").split(".").pop().toLowerCase();

  if (!allowedExtensions.includes(fileExtension) && !file.type.startsWith("image/")) {
    useFeedbackStore.getState().showFeedback(
      `Invalid file type! Allowed types: ${ALLOWED_FILE_EXTENSIONS.join(", ")}, images.`,
      "error"
    );
    return false;
  }

  const limitError = getUploadLimitError(files, [file], {
    maxFileCount: totalFilesAllowed,
    maxSingleFileSizeMB: fileSizeAllowed,
    maxTotalSizeMB: overFileSizeAllowed,
  });
  if (limitError) {
    useFeedbackStore.getState().showFeedback(limitError, "error");
    return false;
  }

  return true;
};

export function getTimestamp(timestamp) {
  if (typeof timestamp === "number") return timestamp;
  if (
    typeof timestamp === "object" &&
    timestamp !== null &&
    timestamp._seconds &&
    timestamp._nanoseconds
  ) {
    return timestamp._seconds * 1000 + timestamp._nanoseconds / 1e6;
  }
  if (typeof timestamp === "string") {
    return new Date(timestamp).getTime();
  }
  return 0;
}

export const getNavigateLink = (uid, space) => {
  return space ? `/spectra/${space.space_id}/chat/${uid}` : `/chat/${uid}`;
};
export const isChatActive = (location, uid, space) => {
  return location.pathname === getNavigateLink(uid, space);
};

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidDomainEmail = (email, domain) => {
  if (!isValidEmail(email)) return false;
  const emailDomain = email.split("@")[1];
  return emailDomain.toLowerCase() === domain.toLowerCase();
};

export function mapValue(x, inMin, inMax, outMin, outMax) {
  return ((x - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
}

export const formatFileSize = (sizeInBytes) => {
  const sizeInMB = sizeInBytes / (1024 * 1024);
  if (sizeInMB >= 1) {
    return `${sizeInMB.toFixed(1)} MB`;
  } else {
    const sizeInKB = sizeInBytes / 1024;
    return `${sizeInKB.toFixed(1)} KB`;
  }
};

export const getFileExtention = (fileName) => {
  if (!fileName) return "";
  const parts = fileName.split(".");
  if (parts.length <= 1) {
    return ""; // No extension
  }
  const fileExtention = parts.pop();
  return fileExtention?.toUpperCase() ?? "";
};

export const scrollToSpectra = (id, duration = 100) => {
  setTimeout(() => {
    const spectraEle = document.getElementById(`spectra-${id}`);
    const container = document.getElementById("spectras-container");
    if (spectraEle && container) {
      const header = document.getElementById("spectra-section-header");
      const offset = header ? header.offsetHeight : 0;
      const topPos = spectraEle.offsetTop - offset;
      container.scrollTo({
        top: Math.max(topPos, 0),
        behavior: "smooth",
      });
    }
  }, duration);
};

// NOTE: The activity-parsing logic in transformContent (RESEARCH_START extraction,
// reduceArrayForContent) is intentionally retained.
// Reason: sourcesData derived here feeds CitationsPreview and YouTubeVideosSection in
// SingleMessage.js.  Old Firestore messages have RESEARCH_START embedded in stored content
// and no separate sources field — removing this parsing would break citation display for all
// existing messages.  This can only be removed after sources are independently persisted
// to Firestore and SingleMessage.js reads them directly.
function reduceArrayForContent(data) {
  const researchData = [];
  const sourcesData = [];

  for (let i = 0; i < data?.length; i++) {
    const item = data[i];

    if (item?.type === "sources") sourcesData.push(...(item?.data || []));
    else researchData.push(item);
  }

  return {
    researchData,
    sourcesData,
  };
}

export function transformContent(text) {
  const researchPattern = /RESEARCH_START:(.*?):RESEARCH_END/gs;
  let parsedData = [];
  let match;

  while ((match = researchPattern.exec(text)) !== null) {
    parsedData.push(match[1].trim());
  }

  // Strip completed RESEARCH_START/END blocks
  let cleanedText = text.replace(researchPattern, "").trim();
  // Strip any unclosed RESEARCH_START block mid-stream (no closing :RESEARCH_END yet)
  cleanedText = cleanedText.replace(/RESEARCH_START:[\s\S]*$/g, "").trim();

  // Strip SSE protocol events (SSE_START:...:SSE_END) — these should never reach
  // stored content.  If found, they indicate a backend leak where an SSE event was
  // appended to full_answer before being persisted to Firestore.
  const ssePattern = /SSE_START:.*?:SSE_END/gs;
  if (ssePattern.test(cleanedText)) {
    console.warn("[transformContent] SSE_START marker found in stored message content — backend leak detected. Stripping before render.", cleanedText.slice(0, 200));
    cleanedText = cleanedText.replace(/SSE_START:.*?:SSE_END/gs, "").trim();
  }

  // Preserve paragraph spacing - only clean up excessive newlines (3+ in a row)
  let finalText = cleanedText.replace(/\n{3,}/g, "\n\n");

  // Check if the text is wrapped in quotes (JSON stringified) and unwrap it
  if (finalText.startsWith('"') && finalText.endsWith('"')) {
    try {
      finalText = JSON.parse(finalText);
    } catch {
      // If parsing fails, keep the original text
    }
  }

  // Normalize single-dollar LaTeX ($CL_{iv}$ -> $$CL_{iv}$$), \(...\), and "** Why...**"
  finalText = normalizeMarkdownMath(finalText);

  parsedData = parsedData?.map((item) => {
    try {
      return stringToJson(item);
    } catch {
      return {};
    }
  });

  const data = {
    ...reduceArrayForContent(parsedData),
    cleanedText: finalText,
  };

  return data;
}

export const getFileIcon = (file) => {
  const fileName = file?.name || file || "";

  if (!fileName || typeof fileName !== "string") {
    return <FileText className="text-ink-blue-600 dark:text-ink-blue-400 w-12 h-12" data-testid="file-alt-icon" />;
  }

  const extension = fileName.split(".").pop()?.toLowerCase() || "";

  if (extension === "pdf") {
    return (
      <div className="flex items-center justify-center w-12 h-12 bg-fill-red-100 dark:bg-fill-red-900/30 rounded-lg" data-testid="pdf-icon">
        <span className="text-ink-red-800 dark:text-ink-red-400 font-bold text-xs">
          PDF
        </span>
      </div>
    );
  }

  if (["doc", "docx"].includes(extension)) {
    return (
      <div className="flex items-center justify-center w-12 h-12 bg-fill-blue-100 dark:bg-fill-blue-900/30 rounded-lg" data-testid="doc-icon">
        <span className="text-ink-blue-600 dark:text-ink-blue-400 font-bold text-xs">
          DOC
        </span>
      </div>
    );
  }

  if (extension === "csv") {
    return (
      <div className="flex items-center justify-center w-12 h-12 bg-fill-green-100 dark:bg-fill-green-900/30 rounded-lg" data-testid="csv-icon">
        <span className="text-ink-green-800 dark:text-ink-green-400 font-bold text-xs">
          CSV
        </span>
      </div>
    );
  }

  return <FileText className="text-ink-blue-600 dark:text-ink-blue-400 w-12 h-12" data-testid="file-alt-icon" />;
};

export function stringToJson(text) {
  // const validJsonStr = text.replace(/'/g, '"');
  const validJsonStr = text;
  const jsonObject = JSON.parse(validJsonStr);

  return jsonObject;
}

/**
 * Render text with styled tags. Valid tags use the theme accent + italic.
 * @param {string} text - The text to render
 * @param {string[]} validTags - Array of valid tags (e.g., ["#compliance", "#hr"])
 * @param {string[]} validTriggers - Array of valid agent triggers (e.g., ["drive", "mail"])
 * @returns {React.ReactNode[]} - Array of text and styled span elements
 */
const _RENDER_URL_RE = /https?:\/\/\S+/g;
const _RENDER_TRAIL = /[.,;:!?)"'<>]+$/;
const _escHtml = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Converts plain text to HTML, wrapping URLs in theme-accent <span> elements.
 * Used by prompt input overlays (Prompt.js, SingleMessage.js) to highlight
 * URLs while typing without replacing the underlying <textarea>.
 *
 * @param {string} text - Raw input text
 * @returns {string} - HTML string safe for dangerouslySetInnerHTML
 */
export function highlightUrlsHtml(text) {
  if (!text) return '';
  const parts = [];
  let last = 0;
  let m;
  const re = new RegExp(_RENDER_URL_RE.source, 'g');
  while ((m = re.exec(text)) !== null) {
    const url = m[0].replace(_RENDER_TRAIL, '');
    const trail = m[0].slice(url.length);
    if (m.index > last) parts.push(_escHtml(text.slice(last, m.index)));
    parts.push(`<span style="color:var(--photon-chat-accent)">${_escHtml(url)}</span>`);
    if (trail) parts.push(_escHtml(trail));
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(_escHtml(text.slice(last)));
  let html = parts.join('');
  
  // Preserve a trailing blank line for overlay-based textareas.
  // Without a sentinel character, a final newline is not rendered visually,
  // so Shift+Enter appears to keep the caret on the previous line.
  if (text.endsWith('\n')) {
    html += '\u200b';
  }

  return html;
}

export function renderTextWithTags(text, validTags = [], validTriggers = []) {
  if (!text) return text;

  // Combined pattern for URLs, #tags, and @triggers
  const pattern = /https?:\/\/\S+|#[a-z][a-z-]*|@[a-zA-Z][a-zA-Z0-9-]*/gi;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const matched = match[0];

    if (matched.startsWith('http')) {
      // Strip trailing punctuation so "https://example.com." doesn't include the period
      const url = matched.replace(_RENDER_TRAIL, '');
      const trail = matched.slice(url.length);
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          style={{ color: 'var(--photon-chat-accent)', textDecoration: 'underline' }}
          onClick={e => e.stopPropagation()}
        >
          {url}
        </a>
      );
      if (trail) parts.push(trail);
    } else if (matched.startsWith('#')) {
      const isValid = validTags.includes(matched.toLowerCase());
      parts.push(isValid
        ? <span key={match.index} style={{ color: 'var(--photon-chat-accent)', fontStyle: 'italic' }}>{matched}</span>
        : matched
      );
    } else if (matched.startsWith('@')) {
      const isValid = validTriggers.some(t => t.toLowerCase() === matched.toLowerCase());
      parts.push(isValid
        ? <span key={match.index} style={{ color: 'var(--photon-chat-accent)', fontStyle: 'italic' }}>{matched}</span>
        : matched
      );
    }

    lastIndex = match.index + matched.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

/**
 * Capitalize the first letter of a string.
 * @param {string} str
 * @returns {string}
 */
export const capitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Derive a readable title from a URL by extracting and capitalizing the domain.
 * Falls back to `fallback` if the URL cannot be parsed.
 *
 * @param {string} url
 * @param {string} [providedTitle] - Use this title when present and non-empty
 * @param {string} [fallback="Link"] - Returned when nothing else works
 * @returns {string}
 */
export const getDefaultTitleFromUrl = (url, providedTitle, fallback = "Link") => {
  if (providedTitle && providedTitle.trim()) return providedTitle.trim();
  if (!url) return fallback;

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.replace(/^www\./, "");
    const cleanDomain = hostname.split(".")[0].replace(/\.com$|\.org$|\.net$/, "");
    return capitalize(cleanDomain);
  } catch {
    return fallback;
  }
};

/**
 * Format a timestamp into a full readable date string.
 * Output example: "Jan 5 2025 3:42 PM EST"
 *
 * @param {number|string|Date} timestamp
 * @returns {string}
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  try {
    const date = new Date(timestamp);
    const formatted = date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
    return formatted.replace(/(\d{4}),/, "$1");
  } catch {
    return "";
  }
};

/**
 * Return a compact relative-time string such as "5m ago", "3h ago", "2d ago".
 *
 * @param {number|string|Date} date
 * @returns {string}
 */
export const getTimeAgo = (date) => {
  try {
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  } catch {
    return "recently";
  }
};
